import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";

/**
 * About — consolidated Manifesto + Why section.
 * One bold statement, three tight principles. No chapter number.
 */
export const About = () => {
  const { t } = useLang();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative isolate overflow-hidden bg-[color:var(--paper)] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <div className="mb-12 flex items-baseline gap-6">
          <span className="eyebrow">{t.about.eyebrow}</span>
          <div className="hairline-rule flex-1" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          data-testid="about-headline"
          className="font-display max-w-[16ch] text-[32px] leading-[1.05] text-[color:var(--ink)] sm:text-[44px] lg:text-[60px]"
        >
          {t.about.title_a}{" "}
          <span className="font-serif-accent italic text-[color:var(--emerald)]">
            {t.about.title_em}
          </span>{" "}
          {t.about.title_b}
        </motion.h2>

        <div className="mt-20 grid grid-cols-1 gap-x-14 gap-y-14 sm:grid-cols-3">
          {t.about.principles.map((p, i) => (
            <motion.div
              key={p.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08 * i }}
              viewport={{ once: true, margin: "-60px" }}
              data-testid={`about-principle-${i}`}
              className="border-t border-[color:var(--rule-strong)] pt-5"
            >
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display mt-3 text-[22px] leading-tight text-[color:var(--ink)]">
                {p.t}
              </h3>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[color:var(--ink-3)]">
                {p.d}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
